import styled from "@emotion/styled";
import { css as className } from "@emotion/css";
import { css } from "@emotion/react";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { EnigmaTokens } from "@type/tokens";
import { Modal } from "@components/modal";
import { internalTokens as staticTokens } from "@constants/tokens";

export const StyledFilterModal = styled(Modal)`
    width: 800px !important;
    .enigma-modal-content {
        padding: 0;
    }
`;
export const StyledFilterMenuWrapper = styled.ul<{ tokens: EnigmaTokens }>`
    padding: ${({ tokens }) => tokens.spacing.spacing200};
    border-right: 1px solid ${({ tokens }) => tokens.color.border.default};
    min-height: 300px;
    max-height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    margin: 0;
`;
export const StyledCustomFilterRenderer = styled.div`
    width: 100%;
`;

interface MenuItem {
    active?: boolean;
    disabled?: boolean;
}

const getConditionalStyles = (active?: boolean, disabled?: boolean) => {
    const tokens = useEnigmaToken();
    if (disabled) {
        return css`
            background-color: ${tokens.color.bg.surface.surfaceDisabled};
            color: ${tokens.color.text.textDisabled};
            cursor: not-allowed;
            &:hover {
                background-color: ${tokens.color.bg.surface.surfaceDisabled};
                color: ${tokens.color.text.textDisabled};
            }
        `;
    }
    if (active) {
        return css`
            background-color: ${tokens.color.bg.fill.fillPrimarySubtle};
            color: ${tokens.color.text.textPrimary};
        `;
    }
};

export const FilterRendererWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

export const removeClass = className`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
`;
export const getFooterClassName = (tokens: EnigmaTokens) => className`
   padding: ${tokens.spacing.spacing100} ${tokens.spacing.spacing200};
`;
export const errorTitle = (tokens: EnigmaTokens) => className`
    color:${tokens.color.text.textDanger};
`;

export const errorIcon = className`
    padding: ${`${staticTokens.spacing.spacing25} ${staticTokens.spacing.spacing50}`};
    width: 24px;
    height: 20px;
`;

export const StyledFilterMenuItem = styled.li<
    MenuItem & { tokens: EnigmaTokens }
>`
    padding: ${`${staticTokens.spacing.spacing100} ${staticTokens.spacing.spacing150}`};
    gap: ${staticTokens.spacing.spacing100};
    font-size: 14px;
    font-weight: 600;
    min-width: 150px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    border-radius: ${staticTokens.radius.radiusMd};
    margin-bottom: ${staticTokens.spacing.spacing50};
    transition: all 0.2s ease;
    &:last-of-type {
        margin-bottom: 0;
    }

    &:hover {
        background-color: ${({ tokens }) =>
            tokens.color.bg.fill.fillPrimarySubtle};
        color: ${({ tokens }) => tokens.color.text.textPrimary};
        .${removeClass} {
            opacity: 1;
            background-color: ${({ tokens }) =>
                tokens.color.bg.fill.fillPrimarySubtle};
            color: ${({ tokens }) => tokens.color.text.textPrimary};
        }
    }
    ${({ disabled, active }) => getConditionalStyles(active, disabled)};
`;

export const FilterCountPlaceHolder = styled.div`
    width: 24px;
`;

export const StyledFilterCount = styled.button<{ tokens: EnigmaTokens }>`
    padding: ${`${staticTokens.spacing.spacing25} ${staticTokens.spacing.spacing50}`};
    border-radius: ${staticTokens.radius.radiusXl};
    background-color: ${({ tokens }) => tokens.color.bg.fill.fillPrimarySubtle};
    font-size: ${staticTokens.typography.fontSize.size200};
    font-weight: ${staticTokens.typography.fontWeight.medium};
    color: inherit;
    line-height: 16px;
    min-width: 24px;
    width: 24px;
    box-sizing: border-box;
    text-align: center;
    cursor: pointer;
    border: none;
    position: relative;
`;

export const StyledModalFooter = styled.div<{ tokens: EnigmaTokens }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${staticTokens.spacing.spacing200};
    border-top: 1px solid ${({ tokens }) => tokens.color.border.default};
    gap: ${staticTokens.spacing.spacing150};
`;

export const StyledModalTitle = styled.div<{ tokens: EnigmaTokens }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${staticTokens.spacing.spacing200};
    border-bottom: 1px solid ${({ tokens }) => tokens.color.border.default};
    flex: 1;
    font-weight: 600;
`;

export const StyledLoaderWrapper = styled.div<{ tokens: EnigmaTokens }>`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    flex: 1;
`;

// classes related to antd
export const modalCloseButtonClass = className`
    .ant-btn-icon {
        display: flex;
    }
`;
export const filterModalBodyClass = className`
    display: flex;
    overflow: auto;
    height: 525px;
    box-sizing: border-box;
    * {
        box-sizing: border-box;
    }
`;

export const filterModalFooterClass = className`
    && {
        margin-top: 0;
    }
`;

export const rangePickerDropdown = (tokens: EnigmaTokens) => className`
    padding: ${tokens.spacing.spacingNone};
    border: ${tokens.spacing.spacingNone};
    border-radius: ${tokens.spacing.spacingNone};
    box-shadow: none !important;
    width: 100%;
    .ant-picker-input {
        border: 1px solid ${tokens.colorBorder};
		border-radius: ${tokens.spacing.spacing50};
		padding: ${tokens.spacing.spacing50} ${tokens.spacing.spacing100};
    }
    .ant-picker-active-bar {
    	margin-left:  ${tokens.spacing.spacingNone};
		opacity: 1;
    }
`;

export const rangePickerPopupContainer = (
    disabled: boolean,
    tokens: EnigmaTokens
) => className`
    left: 0 !important;
	top: 40px !important;
	padding:  ${tokens.spacing.spacing50}  ${tokens.spacing.spacingNone};
	z-index: 1000 !important;
    .ant-picker-panel-container {
        box-shadow: none;
        display:flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .ant-picker-panel {
        border-bottom-width:  ${tokens.spacing.spacingNone} !important;
    }
    .ant-picker-cell-range-hover-start::after{
        left: 0 !important;
    }
    .ant-picker-cell-in-range {
        pointer-events: ${disabled ? "none" : ""};
    }
`;

export const datePickerRow = (tokens: EnigmaTokens) => className`
	display: block;
	overflow-x: hidden;
    overflow-y: auto;
	min-height: 400px;
    margin-top: ${tokens.spacing.spacing150}
}`;

export const desktopPickerMainContainer = className`
	display: block;
	height: 340px;
	position: relative;
}`;
export const datePickerTitleWrapper = (tokens: EnigmaTokens) => className`
	display: flex;
    margin-bottom: ${tokens.spacing.spacing100};
    gap: ${tokens.spacing.spacing200};
}`;
export const datePickerTitle = (tokens: EnigmaTokens) => className`
	flex: 1;
	color: ${tokens.color.text.textPrimary};
}`;

export const timePickerPopup = className`
    z-index: 1200;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
`;

export const desktopTimePickerContainer = className`
    width: 100%;
	margin-top: 20px;
	display: flex;
    align-items: center;
    gap: 16px;
}`;

export const timePicker = (tokens: EnigmaTokens) => className`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.spacing100};
  flex: 1;
  justify-content: flex-start;
`;

// Child elements styles ---------
export const StyledChipGroup = styled.div`
    display: grid;
    gap: ${staticTokens.spacing.spacing200};
    grid-template-columns: 1fr 1fr;
`;

export const StyledDateRangeChipGroup = styled.div<{
    tokens: EnigmaTokens;
}>`
    display: flex;
    gap: ${({ tokens }) => tokens.spacing.spacing100};
    flex-wrap: wrap;
`;

export const StyledChipFilterWrapper = styled.div<{
    tokens: EnigmaTokens;
    hasError: boolean;
}>`
    flex: 1;
    padding: ${({ tokens }) => tokens.spacing.spacing150};
    max-height: ${({ hasError }) => (hasError ? "450px" : "auto")};
    overflow-y: auto;
`;

export const StyledDateRangeChip = styled.div<{
    tokens: EnigmaTokens;
}>`
    flex: 1;
    padding: ${({ tokens }) => tokens.spacing.spacing150};
`;
export const StyledListFilterWrapper = styled.div`
    flex: 1;
`;

export const chipFilterItemClass = (tokens: EnigmaTokens) => className`
    && {
        justify-content: space-between;
        padding: ${tokens.spacing.spacing100};
        border-radius: ${tokens.radius.radiusMd};
    }
`;

export const StyledList = styled.ul<{ showFooter: boolean }>`
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: ${({ showFooter }) => (showFooter ? "383px" : "420px")};
    overflow-y: auto;
`;
export const listItemWrapperClass = className`
    && {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: 420px;
        overflow-y: auto;
    }
`;

export const listItemCheckboxClass = (tokens: EnigmaTokens) => className`
    && {
        display: flex;
        align-items: center;
        justify-content: start;
        padding: ${staticTokens.spacing.spacing150} ${staticTokens.spacing.spacing300};
        border-bottom: 1px solid ${tokens.color.border.default};
        .ant-checkbox+span {
            flex: 1;
            padding-right: 0;
        }
    }
`;
export const StyledListItem = styled.li<{
    active: boolean;
    disabled: boolean;
    tokens: EnigmaTokens;
}>`
    :hover {
        background-color: ${({ tokens }) => {
            return tokens.color.bg.surface.surfacePrimary;
        }};
    }
    ${({ active, disabled, tokens }) => {
        if (active) {
            return css`
                background-color: ${tokens.color.bg.surface.surfacePrimary};
            `;
        }
        if (disabled) {
            return css`
                background-color: ${tokens.color.bg.surface.surfaceDisabled};
                :hover {
                    background-color: ${tokens.color.bg.surface
                        .surfaceDisabled};
                }
            `;
        }
    }};
`;

export const StyledNoResultWrapper = styled.div<{
    tokens: EnigmaTokens;
}>`
    flex: 1;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100% - 72px);
    text-align: center;
    padding: ${({ tokens }) => tokens.spacing.spacing200};
    gap: ${({ tokens }) => tokens.spacing.spacing150};
`;
